import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useCart } from '@/lib/cart';
import { toast } from 'sonner';
import { SHOP } from '@/constants/testIds';
import { X } from 'lucide-react';

const Cart = () => {
  const nav = useNavigate();
  const { refresh: refreshCart } = useCart();
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => api.get('/shop/cart').then(({ data }) => setCart(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const updateQty = async (product_id, quantity) => {
    try {
      const { data } = await api.post('/shop/cart/update', { product_id, quantity });
      setCart(data);
      await refreshCart();
    } catch { toast.error('Update failed'); }
  };

  if (loading) return <div className="sans-container sans-section text-sans_mute">Loading…</div>;
  if (!cart || cart.items.length === 0) return (
    <div className="bg-sans_bone">
      <section className="sans-section">
        <div className="sans-container max-w-2xl">
          <div className="sans-label mb-6">Cart</div>
          <h1 className="sans-h1">Your cart is empty.</h1>
          <p className="sans-body mt-6">Explore our classical formulations.</p>
          <Link to="/products" className="sans-btn-primary mt-10">Browse products</Link>
        </div>
      </section>
    </div>
  );

  return (
    <div className="bg-sans_bone">
      <section className="sans-section">
        <div className="sans-container grid md:grid-cols-12 gap-12">
          <div className="md:col-span-8">
            <div className="sans-label mb-6">Cart</div>
            <h1 className="sans-h1">{cart.items.length} item{cart.items.length !== 1 ? 's' : ''}</h1>

            <div className="mt-12 border-t border-sans_line">
              {cart.items.map((it) => (
                <div key={it.product_id} className="grid grid-cols-12 gap-4 py-8 border-b border-sans_line items-center">
                  <div className="col-span-3 sm:col-span-2 aspect-square bg-sans_bone border border-sans_line overflow-hidden flex items-center justify-center p-2">
                    <img src={it.image} alt={it.name} className="max-w-full max-h-full object-contain" />
                  </div>
                  <div className="col-span-6 sm:col-span-6">
                    <Link to={`/products/${it.slug}`} className="font-serif text-xl text-sans_ink hover:opacity-70">{it.name}</Link>
                    <div className="text-sm text-sans_mute mt-1">₹{it.price}</div>
                    <div className="mt-3 inline-flex items-center border border-sans_line">
                      <button onClick={() => updateQty(it.product_id, Math.max(1, it.quantity - 1))} className="w-8 h-8 text-sans_ink hover:bg-sans_bone">−</button>
                      <div data-testid={SHOP.cartItemQty(it.product_id)} className="w-8 text-center text-sm">{it.quantity}</div>
                      <button onClick={() => updateQty(it.product_id, Math.min(20, it.quantity + 1))} className="w-8 h-8 text-sans_ink hover:bg-sans_bone">+</button>
                    </div>
                  </div>
                  <div className="col-span-3 sm:col-span-4 text-right">
                    <div className="text-sans_ink">₹{it.line_total}</div>
                    <button data-testid={SHOP.cartRemove(it.product_id)} onClick={() => updateQty(it.product_id, 0)} className="mt-3 text-sans_mute hover:text-sans_ink" aria-label="Remove">
                      <X className="w-4 h-4 inline" strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="md:col-span-4">
            <div className="border border-sans_line bg-white p-8 sticky top-24">
              <div className="sans-label mb-6">Summary</div>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-sans_mute">Subtotal</span><span className="text-sans_ink">₹{cart.subtotal}</span></div>
                <div className="flex justify-between"><span className="text-sans_mute">Shipping</span><span className="text-sans_ink">{cart.shipping === 0 ? 'Free' : `₹${cart.shipping}`}</span></div>
                <div className="border-t border-sans_line pt-3 flex justify-between font-serif text-lg"><span className="text-sans_ink">Total</span><span className="text-sans_ink">₹{cart.total}</span></div>
              </div>
              <button onClick={() => nav('/checkout')} data-testid={SHOP.checkoutBtn} className="sans-btn-primary w-full mt-8">
                Proceed to checkout
              </button>
              {cart.subtotal < 999 && (
                <p className="mt-4 text-xs text-sans_mute leading-relaxed">Free shipping on orders above ₹999.</p>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Cart;
